/**
 * The pure parts of the live browser view, and its DevTools connection: the
 * remote Chrome (Browser Use's) is driven over CDP from the buyer's own
 * phone. No React and no DOM beyond `WebSocket` and `fetch`, so the mapping
 * and the focus chain are tested with a fake transport.
 */
import type { Frame, LiveTransport, TransportOptions } from "./transport";

/** The remote phone's viewport (Browser Use browsers are opened at this size). */
export const REMOTE_VIEWPORT = { width: 390, height: 844 } as const;
/** The page's own viewport in it, below the browser's bars: the frame's
 *  shape until its first frame says (Page.screencastFrame's metadata). */
export const PAGE_VIEWPORT = { width: 390, height: 632 } as const;

/** Browser Use's bare DevTools host: control of the browser. */
export const LIVE_VIEW_HOST = /^[a-z0-9-]+\.cdp\.browser-use\.com$/;
/** A Boat browser's live view (boat.ts): its full gated address on the
 *  browser's machine, `wss://…on.boat.dev/live/<id>?_token=…`. */
export function isBoatLiveView(host: string): boolean {
  try {
    const url = new URL(host);
    return url.protocol === "wss:" && /^[a-z0-9-]+\.on\.boat\.dev$/.test(url.hostname) &&
      !url.username && !url.password && !url.port && !url.hash &&
      /^\/live\/[a-f0-9]{32}$/.test(url.pathname) &&
      url.searchParams.size === 1 && !!url.searchParams.get("_token");
  } catch { return false; }
}
/** Whether `host` is a live-view credential the view accepts: one of these
 *  two, for their own providers' endpoints, and nothing else. */
export const isLiveViewHost = (host: string): boolean => LIVE_VIEW_HOST.test(host) || isBoatLiveView(host);

/** One CDP command. Without a `sessionId` it goes to the browser itself. */
export type CdpCall = (
  method: string,
  params?: object,
  sessionId?: string,
) => Promise<unknown>;

type Rect = { left: number; top: number; width: number; height: number };
type Size = { width: number; height: number };

/**
 * Where a finger on the canvas lands in the remote page. The canvas is drawn
 * at the viewport's shape and never scaled to another (a shorter box scrolls
 * instead), so its box maps straight onto the remote viewport.
 */
export function remotePoint(
  canvas: Rect,
  remote: Size,
  clientX: number,
  clientY: number,
) {
  const clamp = (value: number) => Math.min(Math.max(value, 0), 1);
  return {
    x: clamp((clientX - canvas.left) / canvas.width) * remote.width,
    y: clamp((clientY - canvas.top) / canvas.height) * remote.height,
  };
}

/**
 * Where the hidden input sits inside the scrolling box: at the tap. iOS
 * scrolls the focused input above the keyboard, so the tapped spot is what
 * stays in view.
 */
export function inputPosition(
  box: Rect,
  scrollTop: number,
  tap: { x: number; y: number },
) {
  return {
    top: tap.y - box.top + scrollTop,
    left: Math.max(0, tap.x - box.left),
  };
}

/** Typing as key presses, one character at a time: a card field formats as
 *  it is typed into, which a single insert would not trigger. */
export function keyEvents(text: string) {
  return [...text].flatMap((key) => [
    { type: "keyDown", text: key, key },
    { type: "keyUp", key },
  ]);
}

/** The keys a phone keyboard sends that are not characters. */
export const SPECIAL_KEYS: Record<
  "Backspace" | "Enter",
  { key: string; code: string; windowsVirtualKeyCode: number }
> = {
  Backspace: { key: "Backspace", code: "Backspace", windowsVirtualKeyCode: 8 },
  Enter: { key: "Enter", code: "Enter", windowsVirtualKeyCode: 13 },
};

const valueOf = (result: unknown): unknown =>
  (result as { result?: { value?: unknown } } | undefined)?.result?.value;

/** The page's focused element, when it is a frame: its name (which is the
 *  frame's own `window.name`), else null. */
const FOCUSED_FRAME =
  "(() => { const e = document.activeElement; return e && e.tagName === 'IFRAME' ? (e.getAttribute('name') || e.id || '') : null; })()";
/** A tap can leave a frame focused but not its field: focus the first one. */
const FOCUS_FIRST_FIELD =
  "(() => { if (document.activeElement === document.body) { const i = document.querySelector('input:not([type=hidden]), textarea, [contenteditable]'); if (i) i.focus(); } })()";
/** Whether a page or frame holds focus in something that takes text: not a
 *  tick box, a button or a frame (the view looks inside a focused frame
 *  itself, `createFocusChain`). */
const EDITABLE_FOCUSED =
  "(() => { const e = document.activeElement; if (!e) return false; if (e.isContentEditable || e.tagName === 'TEXTAREA') return true; return e.tagName === 'INPUT' && !['checkbox','radio','button','submit','reset','image','file','range','color','hidden'].includes(String(e.type).toLowerCase()); })()";

const MAX_DEPTH = 4;

/**
 * Where typing goes. Card fields (Braintree's) and bank pages are
 * cross-origin frames, each its own target: key events sent to the page never
 * reach them, so they go to the session of the frame that holds focus. The
 * page's focused `<iframe>` names its frame (`window.name`); inside it, the
 * same again for nested frames (a bank's page inside the card scheme's).
 */
export function createFocusChain(call: CdpCall, pageSession: string) {
  const frames = new Map<string, { sessionId: string; name: string }>();
  const evaluate = (expression: string, sessionId: string) =>
    call("Runtime.evaluate", { expression, returnByValue: true }, sessionId).catch(
      () => undefined,
    );

  async function attachFrames() {
    const { targetInfos } = (await call("Target.getTargets")) as {
      targetInfos: { targetId: string; type: string }[];
    };
    const live = targetInfos.filter((target) => target.type === "iframe");
    const ids = new Set(live.map((target) => target.targetId));
    for (const id of frames.keys()) if (!ids.has(id)) frames.delete(id);
    for (const target of live) {
      if (frames.has(target.targetId)) continue;
      const { sessionId } = (await call("Target.attachToTarget", {
        targetId: target.targetId,
        flatten: true,
      })) as { sessionId: string };
      const name = valueOf(await evaluate("window.name", sessionId));
      frames.set(target.targetId, {
        sessionId,
        name: typeof name === "string" ? name : "",
      });
    }
  }

  /** The session typing should go to (undefined: the page itself), leaving
   *  the field focused inside it. */
  return async function resolve(): Promise<string | undefined> {
    await attachFrames();
    let current: string | undefined;
    for (let depth = 0; depth < MAX_DEPTH; depth++) {
      const name = valueOf(await evaluate(FOCUSED_FRAME, current ?? pageSession));
      if (typeof name !== "string") break;
      let next = name
        ? [...frames.values()].find((frame) => frame.name === name)?.sessionId
        : undefined;
      if (!next)
        for (const frame of frames.values()) {
          if (frame.sessionId === current) continue;
          if (valueOf(await evaluate("document.hasFocus()", frame.sessionId))) {
            next = frame.sessionId;
            break;
          }
        }
      if (!next) break;
      current = next;
    }
    if (current) await evaluate(FOCUS_FIRST_FIELD, current);
    return current;
  };
}

/** Whether the remote page, or the frame holding its focus (`sessionId`,
 *  from `createFocusChain`), now has something to type into. */
export async function editableFocused(call: CdpCall, sessionId: string) {
  const result = await call(
    "Runtime.evaluate",
    { expression: EDITABLE_FOCUSED, returnByValue: true },
    sessionId,
  ).catch(() => undefined);
  return valueOf(result) === true;
}

/** A tap, as a mouse click where it landed. Browser Use's Chrome makes no
 *  click of a touch inside a frame: a tap focused Skiddle's reCAPTCHA box
 *  without ticking it, where a click ticks it (30 Sept). */
export function clickEvents(point: { x: number; y: number }) {
  return [
    { type: "mouseMoved", ...point },
    { type: "mousePressed", ...point, button: "left", clickCount: 1 },
    { type: "mouseReleased", ...point, button: "left", clickCount: 1 },
  ];
}

export type Connection = {
  call: (method: string, params?: object, sessionId?: string) => Promise<unknown>;
  /** The page's session, where touch and page-level calls go. */
  pageSession: string;
  close: () => void;
};

/**
 * Opens the browser's DevTools socket and starts the page's screencast.
 * `onFrame` gets each JPEG frame (acknowledged here, or Chrome stops
 * sending); `onClose` is told when the socket drops without `close()`.
 */
export async function openConnection(
  host: string,
  options: {
    ratio: number;
    onFrame: (frame: Frame) => void;
    onClose: () => void;
  },
): Promise<Connection> {
  if (!LIVE_VIEW_HOST.test(host)) throw new Error("Not a live view host");
  const version = (await (await fetch(`https://${host}/json/version`)).json()) as {
    webSocketDebuggerUrl: string;
  };
  const ws = new WebSocket(`wss://${host}${new URL(version.webSocketDebuggerUrl).pathname}`);
  let next = 1;
  let closed = false;
  let ready = false;
  const pending = new Map<
    number,
    { resolve: (value: unknown) => void; reject: (error: Error) => void }
  >();
  const drop = () => {
    for (const { reject } of pending.values()) reject(new Error("Disconnected"));
    pending.clear();
  };
  const call: Connection["call"] = (method, params = {}, sessionId) =>
    new Promise((resolve, reject) => {
      if (ws.readyState !== WebSocket.OPEN) return reject(new Error("Disconnected"));
      const id = next++;
      pending.set(id, { resolve, reject });
      ws.send(JSON.stringify({ id, method, params, ...(sessionId ? { sessionId } : {}) }));
    });
  ws.onmessage = (event) => {
    const message = JSON.parse(String(event.data)) as {
      id?: number;
      result?: unknown;
      error?: { message?: string };
      method?: string;
      sessionId?: string;
      params?: {
        data: string;
        sessionId: number;
        metadata: { deviceWidth: number; deviceHeight: number };
      };
    };
    const waiting = message.id === undefined ? undefined : pending.get(message.id);
    if (message.id !== undefined && waiting) {
      pending.delete(message.id);
      if (message.error) waiting.reject(new Error(message.error.message ?? "CDP error"));
      else waiting.resolve(message.result);
    } else if (message.method === "Page.screencastFrame" && message.params) {
      const { data, metadata, sessionId } = message.params;
      options.onFrame({ data, width: metadata.deviceWidth, height: metadata.deviceHeight });
      ws.send(
        JSON.stringify({
          id: next++,
          method: "Page.screencastFrameAck",
          params: { sessionId },
          sessionId: message.sessionId,
        }),
      );
    }
  };
  ws.onclose = () => {
    drop();
    if (ready && !closed) options.onClose();
  };
  await new Promise<void>((resolve, reject) => {
    ws.onopen = () => resolve();
    ws.onerror = () => reject(new Error("Could not open the browser's connection"));
  });
  const close = () => {
    closed = true;
    ws.close();
  };
  try {
    const { targetInfos } = (await call("Target.getTargets")) as {
      targetInfos: { targetId: string; type: string; openerId?: string }[];
    };
    const pages = targetInfos.filter((target) => target.type === "page");
    // A processor can put the bank's check in its popup (PayPal's SDK).
    // Target order is unspecified: follow the popup to its last open page.
    const page = pages.find((target) => target.openerId && !pages.some((child) => child.openerId === target.targetId)) ?? pages[0];
    if (!page) throw new Error("The browser has no page");
    const { sessionId } = (await call("Target.attachToTarget", {
      targetId: page.targetId,
      flatten: true,
    })) as { sessionId: string };
    await call("Page.enable", {}, sessionId);
    await call(
      "Page.startScreencast",
      {
        format: "jpeg",
        quality: 70,
        maxWidth: Math.round(REMOTE_VIEWPORT.width * options.ratio),
        maxHeight: Math.round(REMOTE_VIEWPORT.height * options.ratio),
        everyNthFrame: 1,
      },
      sessionId,
    );
    ready = true;
    return { call, pageSession: sessionId, close };
  } catch (error) {
    close();
    throw error;
  }
}

/** How long a tap is given to focus something in the remote page. */
const FOCUS_SETTLE_MS = 150;

/**
 * The live view of a Browser Use browser, over CDP (`openConnection`).
 *
 * - A finger is passed on as touch, so the remote page scrolls 1:1 with its
 *   own momentum. (Mouse-wheel events scroll a whole notch, about 125px,
 *   however small the drag.) A still touch is called off and clicked
 *   instead (`clickEvents`): the remote Chrome makes no click of a touch
 *   inside a frame.
 * - Typing goes key by key to the frame holding focus (`createFocusChain`).
 */
export async function openCdpTransport(host: string, options: TransportOptions): Promise<LiveTransport> {
  const connection = await openConnection(host, options);
  const resolveTyping = createFocusChain(connection.call, connection.pageSession);
  let typing: string | undefined;
  const send = (method: string, params: object, sessionId?: string) =>
    connection.call(method, params, sessionId ?? connection.pageSession).catch(() => undefined);
  const touch = (type: string, point?: { x: number; y: number }) =>
    send("Input.dispatchTouchEvent", { type, touchPoints: point ? [point] : [] });
  return {
    drag(phase, point) {
      touch(phase === "start" ? "touchStart" : phase === "move" ? "touchMove" : "touchEnd", phase === "end" ? undefined : point)
        .catch(() => undefined);
    },
    async tap(point, focus) {
      // The touch is called off, so nothing is clicked twice, and clicked
      // where it landed.
      await touch("touchCancel");
      for (const event of clickEvents(point)) await send("Input.dispatchMouseEvent", event);
      if (!focus) return false;
      await new Promise((resolve) => setTimeout(resolve, FOCUS_SETTLE_MS));
      // Typing goes to the frame holding focus.
      const target = await resolveTyping().catch(() => undefined);
      if (!(await editableFocused(connection.call, target ?? connection.pageSession))) return false;
      typing = target;
      return true;
    },
    type(text) {
      const target = typing;
      (async () => {
        for (const key of keyEvents(text)) await send("Input.dispatchKeyEvent", key, target);
      })().catch(() => undefined);
    },
    key(name) {
      const key = SPECIAL_KEYS[name];
      const target = typing;
      send("Input.dispatchKeyEvent", { type: "keyDown", ...key }, target)
        .then(() => send("Input.dispatchKeyEvent", { type: "keyUp", ...key }, target))
        .catch(() => undefined);
    },
    // Browser Use's screencast shows the page whole, whichever check.
    show() {},
    close: connection.close,
  };
}
